// Three things for .son-hero (see base/_son-hero.scss).
//
// 1. The image fade: toggles .is-revealed while the section's top edge is
//    above the middle of the screen, so the image fades back out when you
//    scroll up again. Runs in every browser - the fade itself is a plain CSS
//    transition.
//
// 2. Tagging each hero with .son-hero--enters when it starts below the middle
//    of the screen, which shortens the hold before its content enters. A hero
//    at the top of a page never crosses that line.
//
// 3. Scrubbed-animation fallback for the rest of the intro. Browsers with
//    CSS scroll-driven animations (Chromium 115+, Safari 26+) run it on the
//    compositor and never execute this. Firefox gets an
//    IntersectionObserver-gated rAF loop that writes the per-phase progress
//    (0..1) to --son-hero-pan-p and --son-hero-heading-exit-p.

const SUPPORTS_SCROLL_TIMELINE =
    window.CSS && CSS.supports && CSS.supports('animation-timeline: view()');

// scrolled distance per phase, as fractions of the viewport height
// (keep in sync with the $son-hero-* values in base/_son-hero.scss)
const CONTENT_START = 0.32; // the crop pan runs from here to the section end
const HEADING_EXIT_SPAN = 0.5; // heading travels -50svh over 50vh = native speed

// the entry-keyed variant (.son-hero--enters), as a fraction of the viewport
const ENTER_CONTENT_START = 0.08;

// The fade fires as the section's top edge crosses the middle of the screen.
// The scroll gate keeps a hero at the top of a page, which is already past
// that line at load, from firing before the user does anything; on the way
// back up it is what fades the image out again.
const FADE_TRIGGER = 0.5;
const FADE_MIN_SCROLL = 0.05;

function bindHeroFade(hero) {
    if (hero.dataset.sonHeroFadeReady) {
        return;
    }
    hero.dataset.sonHeroFadeReady = 'true';

    const check = () => {
        const vh = window.innerHeight;

        hero.classList.toggle(
            'is-revealed',
            hero.getBoundingClientRect().top <= vh * FADE_TRIGGER && window.scrollY >= vh * FADE_MIN_SCROLL
        );
    };

    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    check();
}

// runs for every hero, in every browser: the class picks the CSS ranges
function tagHeroPlacement(hero) {
    const top = hero.getBoundingClientRect().top + window.scrollY;

    hero.classList.toggle('son-hero--enters', top > window.innerHeight * 0.5);
}

function bindHero(hero) {
    if (hero.dataset.sonHeroReady) {
        return;
    }
    hero.dataset.sonHeroReady = 'true';

    let visible = false;
    let running = false;

    const frame = () => {
        const rect = hero.getBoundingClientRect();
        const scrolled = Math.max(-rect.top, 0);
        const vh = window.innerHeight;
        const clamp = (value) => Math.min(Math.max(value, 0), 1).toFixed(4);

        // mirrors the two animation-range sets in the stylesheet
        const entering = hero.classList.contains('son-hero--enters');
        const contentStart = entering ? ENTER_CONTENT_START : CONTENT_START;

        hero.style.setProperty('--son-hero-pan-p', clamp((scrolled - vh * contentStart) / Math.max(rect.height - vh - vh * contentStart, 1)));
        hero.style.setProperty('--son-hero-heading-exit-p', clamp((scrolled - vh * contentStart) / (vh * HEADING_EXIT_SPAN)));

        if (visible) {
            requestAnimationFrame(frame);
        } else {
            running = false;
        }
    };

    const kick = () => {
        if (!running) {
            running = true;
            requestAnimationFrame(frame);
        }
    };

    new IntersectionObserver((entries) => {
        visible = entries.some((entry) => entry.isIntersecting);
        if (visible) {
            kick();
        }
    }).observe(hero);

    kick(); // set the initial state even when the hero starts off screen
}

function initHeroes(root = document) {
    root.querySelectorAll('.son-hero').forEach(tagHeroPlacement);
    root.querySelectorAll('.son-hero').forEach(bindHeroFade);

    if (!SUPPORTS_SCROLL_TIMELINE) {
        root.querySelectorAll('.son-hero').forEach(bindHero);
    }
}

document.addEventListener('DOMContentLoaded', () => initHeroes());

// a breakpoint change can move a hero across that line
window.addEventListener('resize', () => {
    document.querySelectorAll('.son-hero').forEach(tagHeroPlacement);
});

// re-bind after Elementor editor re-renders an element
window.addEventListener('elementor/frontend/init', () => {
    if (window.elementorFrontend && window.elementorFrontend.hooks) {
        window.elementorFrontend.hooks.addAction('frontend/element_ready/global', ($element) => {
            initHeroes($element[0] || document);
        });
    }
});
